import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload } from "lucide-react";
import axios from "axios";
import { formatApiError, API, authHeaders } from "@/hooks/useAdminData";

import { resolveImg } from "@/config";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SiteSettings({ siteSettings, loadData }) {
  const [heroUploading, setHeroUploading] = useState(false);
  const [aboutUploading, setAboutUploading] = useState(false);

  const uploadHeroImage = async (file) => {
    if (!file) return;
    setHeroUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/settings/upload-hero`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data", ...authHeaders() },
      });
      loadData();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    } finally {
      setHeroUploading(false);
    }
  };

  const uploadAboutImage = async (file) => {
    if (!file) return;
    setAboutUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/settings/upload-about`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data", ...authHeaders() },
      });
      loadData();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    } finally {
      setAboutUploading(false);
    }
  };

  return (
    <>
      <h3 className="text-lg font-bold text-[#1A1A1A] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Site Settings</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-100 rounded-2xl p-6" data-testid="hero-image-setting">
          <h4 className="font-semibold text-[#1A1A1A] mb-3">Homepage Hero Image</h4>
          <p className="text-[#4A4A4A] text-xs mb-4">This is the main background photo visitors see on the homepage.</p>
          {siteSettings.hero_image && (
            <div className="rounded-xl overflow-hidden mb-4 h-[180px]">
              <img src={resolveImg(siteSettings.hero_image)} alt="Current hero" className="w-full h-full object-cover" />
            </div>
          )}
          <Label className="text-sm font-medium flex items-center gap-2 mb-2"><Upload size={14} /> Upload New Hero Photo</Label>
          <Input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={e => { if (e.target.files[0]) uploadHeroImage(e.target.files[0]); }}
            className="rounded-xl"
            disabled={heroUploading}
            data-testid="hero-image-upload"
          />
          {heroUploading && <p className="text-xs text-[#E8922F] mt-2">Uploading...</p>}
        </div>

        <div className="bg-white border border-gray-100 rounded-2xl p-6" data-testid="about-image-setting">
          <h4 className="font-semibold text-[#1A1A1A] mb-3">Homepage About Section Image</h4>
          <p className="text-[#4A4A4A] text-xs mb-4">This photo appears in the "About Us" preview on the homepage.</p>
          {siteSettings.about_image && (
            <div className="rounded-xl overflow-hidden mb-4 h-[180px]">
              <img src={resolveImg(siteSettings.about_image)} alt="Current about" className="w-full h-full object-cover" />
            </div>
          )}
          <Label className="text-sm font-medium flex items-center gap-2 mb-2"><Upload size={14} /> Upload New About Photo</Label>
          <Input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={e => { if (e.target.files[0]) uploadAboutImage(e.target.files[0]); }}
            className="rounded-xl"
            disabled={aboutUploading}
            data-testid="about-image-upload"
          />
          {aboutUploading && <p className="text-xs text-[#E8922F] mt-2">Uploading...</p>}
        </div>
      </div>
    </>
  );
}
